import get from 'lodash/get';
import min from 'lodash/min';
import omit from 'lodash/omit';
import moment from 'moment';
import {useEffect,useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Lock from '@mui/icons-material/Lock';

import useIsLoggedIn from '../../hooks/useIsLoggedIn';
import {useCreateAppointmentMutation} from '../../redux/services/appointmentApi';
import {useGetCurrentPatientQuery, useUpdatePatientMutation} from '../../redux/services/patientApi';
import {useGetProvidersQuery} from '../../redux/services/providerApi';
import {useCreateCustomerMutation} from '../../redux/services/paymentApi';
import {useGetHighlightsQuery} from '../../redux/services/providerHighlightApi';
import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import {useCreateSessionMutation} from '../../redux/services/sessionApi';
import {logout} from '../../redux/reducers/auth';
import {track} from '../../redux/reducers/analytics';
import {
  complete,
  next,
  previous,
  reset,
  setError,
  setHasGuardian,
  setProviderId,
  setScreenIndex,
  setSessionTypeId,
} from '../../redux/reducers/patientSignup';
import {reset as resetAppointmentForm} from '../../redux/reducers/newAppointmentForm';
import {setProviderFilters} from '../../redux/reducers/providers';
import selectCurrentUser from '../../redux/selectors/selectCurrentUser';

import GuardianInfo from '../../components/patientSignup/GuardianInfo';
import ChooseProviderScreen from '../../components/patientSignup/ChooseProviderScreen';
import NewAppointmentForm from '../../components/scheduling/NewAppointmentForm';
import PatientSignupLayout from '../../components/patientSignup/PatientSignupLayout/PatientSignupLayout';
import ContactInfo from '../../components/patientSignup/ContactInfo';
import ConsentForms from '../../components/patientSignup/ConsentForms';
import PaymentForm from '../../components/patientSignup/PaymentForm';
import ReferralSourceForm from '../../components/patientSignup/ReferralSourceForm';
import {shuffle} from "../../helpers/signupRandomBackground";

const screens = ({
  props,
  id,
  user,
  schedulingVersion,
  createPatient,
  updatePatient,
  createCustomer,
  createAppointment,
  navigate,
  forceManualScheduling = false,
}) => ([
  {
    key: 'guardian',
    title: 'Guardian Information',
    subtitle: 'Please enter your information as the guardian of the individual you are registering for.',
    buttonText: null,
    hideBackButton: true,
    component: GuardianInfo,
    useLayout: true,
    step: 2,
    backgroundColor: '#F9F9F9',
    onSubmit: async (formData) => {
      props.track({eventName: 'signup.addedGuardianInfo'});
      const {error} = await updatePatient({
        id,
        body: {
          onboarding_step: 'DEPENDENT',
          has_guardian: formData.hasGuardian,
          guardian_first_name: formData.firstName,
          guardian_last_name: formData.lastName,
          guardian_email: formData.email,
          guardian_phone: formData.phone,
          guardian_relation: formData.relation,
        }
      });
      if (!formData.hasGuardian) props.setHasGuardian(false);
      if (error) {
        props.setError({ message: get(error, 'data.errors', 'Unknown error') });
      } else {
        props.setError(null);
        props.next();
      }
    },
  },
  {
    key: 'chooseProvider',
    title: 'Choose Your Provider',
    subtitle: (
      user && user.firstName
      ? `Who would you like to book a session with, ${user.firstName}? You can search by first name, last name, zip code, or city.`
      : 'Who would you like to book a session with? You can search by first name, last name, zip code, or city.'
    ),
    buttonText: null,
    hideBackButton: props.hasGuardian ? false : true,
    component: ChooseProviderScreen,
    useLayout: false,
    width: '100%',
    autoFocus: true,
    step: props.hasGuardian ? 3 : 2,
    backgroundColor: '#F9F9F9',
    onSubmit: async (formData) => {
      props.track({eventName: 'signup.selectedProvider'});
      const {providerId} = formData;
      if (!providerId) {
        props.setError({ message: 'Please select a provider' });
      } else {
        const resp = await updatePatient({
          id,
          body: {
            selectedProviderId: providerId,
            onboarding_step: 'SELECTED_PROVIDER',
          },
        });
        const {data} = resp;
        if (resp.error && resp.error.status) {
          const message =  get(resp, 'error.data.errors', 'Unknown error');
          props.track({eventName: 'signup.error', message});
          props.setError({ message});
        } else {
          props.setError(null);
          props.next();
        }
      }
    }
  },
  {
    key: 'newAppointmentForm',
    title: null,
    subtitle: null,
    buttonText: null,
    hideBackButton: true,
    component: NewAppointmentForm,
    useLayout: false,
    step: props.hasGuardian ? 4 : 3,
    hideBodyText: true,
    allowSkipSessionType: true,
    hideScheduleAutomaticallyButton: true,
    backgroundColor: '#F9F9F9',
    onSubmit: async () => {
      props.track({eventName: 'signup.scheduled'});
      const res = await updatePatient({
        id,
        body: {onboarding_step: 'PICKED_TIME'}
      });
      props.next(); // newAppointmentForm reducer handles the data
      props.setError(null);
    },
    // special props for the NewAppointmentForm
    false: true,
    hideBack: false,
    onPrevScreen: props.previous,
  },
  {
    key: 'contactInfo',
    title: 'Booking Request Summary',
    subtitle: null,
    buttonText: null,
    hideBackButton: false,
    component: ContactInfo,
    useLayout: true,
    displayAppointmentSummary: true,
    step: props.hasGuardian ? 5 : 4,
    backgroundColor: '#F9F9F9',
    onSubmit: async (formData) => {
      props.track({eventName: 'signup.addedContactInfo'});
      const {error} = await updatePatient({
        id,
        body: {
          ...formData,
          onboarding_step: 'CONTACT_INFO_SAVED',
        }
      });
      if (error) {
        props.track({eventName: 'signup.error', message: get(error, 'data.errors', 'Unknown error')});
        props.setError({ message: get(error, 'data.errors', 'Unknown error') });
      } else {
        props.setError(null);
        props.next();
      }
    }
  },
  {
    key: 'consentInfo',
    title: 'Consent Forms',
    subtitle: 'We value your trust, safety, and privacy. Please read the following consent forms carefully.',
    buttonText: null,
    hideBackButton: false,
    component: ConsentForms,
    useLayout: true,
    displayAppointmentSummary: true,
    step: props.hasGuardian ? 6 : 5,
    backgroundColor: '#F9F9F9',
    onSubmit: async (formData) => {
      props.track({eventName: 'signup.consented'});
      const {error} = await updatePatient({
        id,
        body: {
          ...formData,
          onboarding_step: 'CONTRACT_SIGNED',
          dateOfBirth: moment(formData.dateOfBirth).format('YYYY-MM-DD'),
          contractDate: moment().format('YYYY-MM-DD')
        }
      });
      if (error) {
        props.track({eventName: 'signup.error', message: get(error, 'data.errors', 'Unknown error')});
        props.setError({ message: get(error, 'data.errors', 'Unknown error') });
      } else {
        props.setError(null);
        props.next();
      }
    }
  },
  {
    key: 'paymentInfo',
    title: 'Enter Payment Information',
    TitleIcon: Lock,
    subtitle: 'Nothing is charged at this time. Payments are securely processed only after each session with your provider is completed.',
    buttonText: null,
    hideBackButton: false,
    component: PaymentForm,
    useLayout: true,
    displayAppointmentSummary: true,
    step: props.hasGuardian ? 7 : 6,
    backgroundColor: '#F9F9F9',
    onSubmit: async (formData) => {
      props.track({eventName: 'signup.addedPaymentInfo'});
      console.log('formData', formData);
      const {stripeToken, customer_id, is_stax} = formData;
      const params = is_stax ? {customer_id, is_stax} : {stripeToken};
      const {error} = await createCustomer(params);
      if (error) {
        props.track({eventName: 'signup.error', message: get(error, 'data.errors', 'Unknown error')});
        props.setError({ message: get(error, 'data.errors', 'Unknown error') });
        return;
      }
      // if (!formData.promoCode) return props.next();
      // const {auth: { id } } = props;
      let body = {onboarding_step: 'PAYMENT_METHOD_SAVED'};
      if (formData.promoCode) body.promoCode = formData.promoCode;
      const result = await updatePatient({ id, body });
      props.setError(null);
      props.next();
    }
  },
  {
    key: 'referralSource',
    title: null,
    subtitle: null,
    // subtitle: 'This helps us give you the best possible care.',
    buttonText: null,
    hideBackButton: false,
    component: ReferralSourceForm,
    useLayout: true,
    displayAppointmentSummary: true,
    step: props.hasGuardian ? 8 : 7,
    backgroundColor: '#F9F9F9',
    onSubmit: async (formData) => {
      props.track({eventName: 'signup.submittedFinalStep'});
      if (!formData?.optionalNote && !formData?.opt_note) {
        return props.setError({message: 'Please provide a short message to the provider to explain how we can help you.'});
      }
      const {error} = await updatePatient({
        id,
        body: {
          ...formData,
          onboarding_step: 'SCHEDULED',
          selectedProviderId: props.selectedProviderId,
        },
      });
      if (error) {
        props.track({eventName: 'signup.error', message: get(error, 'data.errors', 'Unknown error')});
        props.setError({ message: get(error, 'data.errors', 'Unknown error') });
      }
      if (schedulingVersion === 'v2') {
        const session = {
          provider_id: props.selectedProviderId,
          session_type_id: props.sessionTypeId,
          provider_availability_id: props.providerAvailabilityId,
          start_time: moment(`${props.date} ${props.time}`, 'MM/DD/yyyy hh:mm a')
            .toISOString(),
          is_eval: true,
          location_type: props.locationType,
        };
        const sessionReq = await createAppointment({body: session});
        if (sessionReq.error) {
          props.track({eventName: 'signup.error', message: 'Error scheduling appointment'});
          return props.setError({ message: 'Error scheduling appointment'});
        }
      } else {
        const appointment = {
          provider_id: props.selectedProviderId,
          session_type_id: props.sessionTypeId,
          start_time: moment(`${props.date} ${props.time}`, 'MM/DD/yyyy hh:mm a')
            .toISOString(),
        };
        if (!forceManualScheduling) {
          const appointmentReq = await createAppointment(appointment);
          if (appointmentReq.error) {
            props.track({eventName: 'signup.error', message: 'Error scheduling appointment'});
            return props.setError({ message: 'Error scheduling appointment'});
          }
        }
      }
      const completeReq = await updatePatient({
        id,
        body: {completedOnboarding: true},
      });
      if (completeReq.error) {
        props.track({eventName: 'signup.error', message: 'Error completing your request.'});
        return props.setError({ message: 'Error completing your request.'});
      }
      props.resetAppointmentForm();
      props.setError(null);
      props.track({eventName: 'signup.completed'});
      navigate('/clients/onboarding/completed');
    }
  },
]);

const handleBack = ({screenIndex ,props}) => e => {
  if (screenIndex === 0) return null;
  props.previous();
  props.setError(null);
};

const parseSearchParams = searchParams => {
  const alreadyScheduled = (
    searchParams.has('alreadyScheduled')
    ? searchParams.get('alreadyScheduled') === 'true'
    : false
  );
  const preselectedProviderId = (
    searchParams.has('selectedProviderId')
    ? searchParams.get('selectedProviderId')
    : null
  );
  return {alreadyScheduled, preselectedProviderId};
};

const getNumSteps = ({props}) => {
  let numSteps = 7;
  if (props.hasGuardian) numSteps = numSteps + 1;
  // if (alreadyScheduled) numSteps = numSteps - 1;
  return numSteps;
};

// skip provider selection once if a provider was already selected
const useSkipProviderSelection = ({
  props, 
  alreadyScheduled, 
  preselectedProviderId,
  skippedProviderScreenOnce,
  setSkippedProviderScreenOnce,
}) => {
  const screenIndex = props?.screenIndex;
  useEffect(() => {
    if (alreadyScheduled && preselectedProviderId && !skippedProviderScreenOnce) {
      const providerScreenIndex = props.hasGuardian ? 1 : 0;
      props.setProviderId({providerId: parseInt(preselectedProviderId, 10)});
      if (screenIndex === providerScreenIndex) {
        setSkippedProviderScreenOnce(true);
        props.setScreenIndex({screenIndex: providerScreenIndex + 1});
      }
    }
  }, [screenIndex]);
};

// skip scheduling screen once if an appointment was already selected
const useSkipScheduling = ({
  props, 
  alreadyScheduled, 
  skippedSchedulingOnce,
  setSkippedSchedulingOnce,
}) => {
  const screenIndex = props?.screenIndex;
  useEffect(() => {
    if (alreadyScheduled && !skippedSchedulingOnce) {
      const schedulingScreenIndex = props.hasGuardian ? 2 : 1;
      if (screenIndex === schedulingScreenIndex) {
        setSkippedSchedulingOnce(true);
        props.setScreenIndex({screenIndex: schedulingScreenIndex + 1});
      }
    }
  }, [screenIndex]);
};

const PatientOnboardingPage = props => {
  const {selectedProviderId, sessionTypeId} = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [skippedProviderScreenOnce, setSkippedProviderScreenOnce] = useState(false);
  const [skippedSchedulingOnce, setSkippedSchedulingOnce] = useState(false);
  const [oldCreateAppointment, appointmentResult] = useCreateAppointmentMutation();
  const [newCreateAppointment] = useCreateSessionMutation();
  const [updatePatient, updateResult] = useUpdatePatientMutation();
  const [createCustomer] = useCreateCustomerMutation();
  const userQuery = useGetCurrentPatientQuery(null, {skip: false});
  const highlightsQuery = useGetHighlightsQuery(null, {skip: false});
  const sessionTypesQuery = useGetSessionTypesQuery(
    {providerId: selectedProviderId},
    {skip: !selectedProviderId}
  );
  const providersQuery = useGetProvidersQuery(
    {providerId: selectedProviderId},
    {skip: !selectedProviderId}
  );
  const {alreadyScheduled, preselectedProviderId} = parseSearchParams(searchParams);
  const user = get(userQuery, 'data', null);
  const id = get(props, 'auth.id') || get(user, 'id');
  const isLoggedIn = useIsLoggedIn();
  const isDone = get(user, 'completed_onboarding', false);
  useEffect(() => {
    shuffle(8);
  }, []);
  useEffect(() => {
    if (!isLoggedIn) navigate('/login');
    if (isDone) navigate('/clients/onboarding/completed');
  }, [isLoggedIn, isDone, navigate]);
  // re-fetch user whenever the page changes
  useEffect(() => {
    userQuery.refetch();
  }, [props.screenIndex]);
  useSkipProviderSelection({  
    props, 
    alreadyScheduled, 
    preselectedProviderId,
    skippedProviderScreenOnce,
    setSkippedProviderScreenOnce,
  });
  useSkipScheduling({
    props,
    alreadyScheduled,
    skippedSchedulingOnce,
    setSkippedSchedulingOnce
  });

  const providers = get(providersQuery, 'data', []);
  const selectedProvider = providers.find(p => p.id === selectedProviderId);
  const sessionType = (
    sessionTypeId
    ? get(sessionTypesQuery, 'data', []).find(s => s.id === sessionTypeId)
    : null
  );
  const forceManualScheduling = (
    props.scheduleManually
    || sessionType?.hide_availability
  );
  const schedulingVersion = !!props.providerAvailabilityId ? 'v2' : 'v1';
  const createAppointment = (
    schedulingVersion === 'v2'
    ? newCreateAppointment
    : oldCreateAppointment
  );
  const rawScreens = screens({
    props,
    id,
    user,
    schedulingVersion,
    createAppointment,
    createCustomer,
    updatePatient,
    alreadyScheduled,
    navigate,
    forceManualScheduling,
  });
  const includedScreens = rawScreens
    // exclude guardian screen if patient does not have a guardian
    .filter(s => (
      !['guardian'].includes(s.key)
      || (s.key === 'guardian' && props.hasGuardian)
    ));
    // .map((s, i) =>
    //   alreadyScheduled && !['guardian', 'chooseProvider'].includes(s.key)
    //   ? {...s, step: s.step - 1}
    //   : s
    // );
  const screenIndex = min([props.screenIndex, includedScreens.length -1]);
  const screen = get(includedScreens, `[${screenIndex}]`);
  const childProps = {
    ...props,
    ...omit(screen, 'component'),
    user,
    numSteps: getNumSteps({props}),
    date: moment(props.date),
    highlights: get(highlightsQuery, 'data', []),
    isLoading: (
      get(userQuery, 'isLoading')
      || get(appointmentResult, 'isLoading')
      || get(updateResult, 'isLoading')
    ),
    selectedProvider,
    selectedProviderId,
    sessionTypes: get(sessionTypesQuery, 'data', []),
    sessionTypeId,
    sessionType,
    onSelectProviderId: id => {
      if (!id) return;
      props.setProviderId(id);
    },
    onBack: handleBack({screenIndex, props}),
    setProviderFilters: props.setProviderFilters,
    backButtonPlacement: 'top',
    isNewPatient: true,
  };
  // console.log('childProps', childProps);
  const Screen = get(screen, 'component');
  if (!Screen) return <></>;
  return (
    <PatientSignupLayout {...childProps}>
      <Screen
        {...childProps}
        hideStepper={
          // hide stepper for appointment form
          screen.key === 'newAppointmentForm'
          ? true
          : childProps.hideStepper
        }
      />
    </PatientSignupLayout>
  );
};

PatientOnboardingPage.defaultProps = {

};

PatientOnboardingPage.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

const mapState = state => ({
  auth: selectCurrentUser()(state),
  error: get(state, 'patientSignup.error', null),
  screenIndex: get(state, 'patientSignup.screenIndex', 0),
  hasGuardian: get(state, 'patientSignup.hasGuardian'),
  selectedProviderId: get(state, 'patientSignup.providerId', null),
  sessionTypeId: get(state, 'newAppointmentForm.sessionTypeId', null),
  date: get(state, 'newAppointmentForm.date', null),
  time: get(state, 'newAppointmentForm.time', null),
  locationType: get(state, 'newAppointmentForm.locationType', null),
  // for V2 scheduling system
  providerAvailabilityId: get(state, 'newAppointmentForm.providerAvailabilityId', null),
  scheduleManually: get(state, 'newAppointmentForm.scheduleManually', null),
  providerFilters: get(state, 'providers.providerFilters', {}),
});

const mapDispatch = dispatch => ({
  next: params => {
    if (window && window.scrollTo) window.scrollTo(0, 0);
    dispatch(next(params));
  },
  previous: params => dispatch(previous(params)),
  setScreenIndex: params => dispatch(setScreenIndex(params)),
  complete: params => dispatch(complete(params)),
  logout: params => dispatch(logout(params)),
  reset: params => {
    dispatch(reset(params));
    dispatch(resetAppointmentForm(params));
  },
  setError: params => dispatch(setError(params)),
  setHasGuardian: params => dispatch(setHasGuardian(params)),
  setSessionTypeId: params => dispatch(setSessionTypeId(params)),
  setProviderId: params => dispatch(setProviderId(params)),
  setProviderFilters: params => dispatch(setProviderFilters(params)),
  resetAppointmentForm: params => dispatch(resetAppointmentForm(params)),
  // Analytocs
  track: params => dispatch(track(params)),
});

export default connect(mapState, mapDispatch)(PatientOnboardingPage);
